import React, { useState } from 'react';

import { Modal, Spin, Button, Form, Input } from 'antd';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { BlueButton } from '@share/components';
import { FunctionalRootState } from '@share/utils';
import { ModalConfirmation } from '../../../modal-confirmation';

import './style.scss';

export interface ModalShareAllProps {
  visible: boolean;
  isShare: boolean;
  loading: boolean;
  shareConfirmationMessage?: string;
  onCancel: () => void;
  onShare: (members: string[], comment: string) => void;
  onDownload: (comment: string) => void;
}

export function ModalShareAll({
  visible,
  isShare,
  loading,
  shareConfirmationMessage,
  onCancel,
  onShare,
  onDownload,
}: ModalShareAllProps) {
  const intl = useIntl();
  const { account } = useSelector((state: FunctionalRootState) => state.loginStore);
  const [form] = Form.useForm();

  const [comment, setComment] = useState('');
  const [pendingMembers, setPendingMembers] = useState<string[]>([]);
  const [showShareConfirmation, setShowShareConfirmation] = useState(false);

  const onFinishShare = (values: { names: string[] }) => {
    const names = values.names || [];
    setPendingMembers(names);
    setShowShareConfirmation(true);
  };

  const onClickShare = async () => {
    try {
      const values = await form.validateFields();
      onFinishShare(values as { names: string[] });
    } catch {
      // Validation errors are shown by antd Form automatically.
    }
  };

  const onClose = () => {
    setComment('');
    setPendingMembers([]);
    setShowShareConfirmation(false);
    onCancel();
  };

  const onConfirmShare = () => {
    setShowShareConfirmation(false);
    onShare(pendingMembers, comment);
  };

  const onCancelShareConfirm = () => {
    setShowShareConfirmation(false);
  };

  const formItemLayoutWithOutLabel = {
    wrapperCol: {
      xs: { span: 24, offset: 0 },
      sm: { span: 20, offset: 4 },
    },
  };

  const fontFamily = account?.fontFamily;
  const secondaryFontFamily = account?.secondaryFontFamily;

  return (
    <Modal
      className={`modal-share ${fontFamily ? `${fontFamily}-font` : ''} ${secondaryFontFamily ? `secondary-${secondaryFontFamily}-font` : ''}`}
      open={visible}
      footer={null}
      onCancel={onClose}
      wrapClassName="modal-share__wrapper"
      width={500}
      style={{ top: 20 }}
      zIndex={1080}
    >
      {isShare ? (
        <Form
          form={form}
          name="dynamic_form_item"
          style={{ width: '100%' }}
          {...formItemLayoutWithOutLabel}
          initialValues={{ names: [''] }}
        >
          <h4 className="modal-share__title secondary-font">
            <FormattedMessage id="enter.member.id.or.email" />
          </h4>
          <Form.List
            name="names"
            rules={[
              {
                validator: async (_, names) => {
                  if (!names || names.length < 1) {
                    return Promise.reject(new Error('At least 1 recipient'));
                  }
                },
              },
            ]}
          >
            {(fields, { add, remove }, { errors }) => (
              <>
                {fields.map((field) => (
                  <Form.Item {...formItemLayoutWithOutLabel} required={false} key={field.key}>
                    <Form.Item
                      {...field}
                      validateTrigger={['onChange', 'onBlur']}
                      rules={[
                        {
                          required: true,
                          whitespace: true,
                          message: <FormattedMessage id="please.input.member.id.or.email.or.delete" />,
                        },
                      ]}
                      noStyle
                    >
                      <Input
                        placeholder={intl.formatMessage({ id: 'please.input.member.id.or.email' })}
                        style={{ width: '90%' }}
                      />
                    </Form.Item>
                    {fields.length > 1 ? (
                      <MinusCircleOutlined className="dynamic-delete-button" onClick={() => remove(field.name)} />
                    ) : null}
                  </Form.Item>
                ))}
                <Form.Item>
                  <Button type="dashed" onClick={() => add()} style={{ width: '90%' }} icon={<PlusOutlined />}>
                    <FormattedMessage id="add.another.member.id.or.email" />
                  </Button>
                  <Form.ErrorList errors={errors} />
                </Form.Item>
              </>
            )}
          </Form.List>

          <h4 className="modal-share__title secondary-font">
            <FormattedMessage id="results.hotel.card.compare.modal.comment.title" />
          </h4>
          <div className="modal-share__hotels">
            <Input.TextArea rows={4} value={comment || ''} onChange={(e) => setComment(e.target.value)} />
          </div>

          <div className="modal-share__buttons">
            <BlueButton disabled={loading} onClick={onClickShare}>
              {loading ? <Spin /> : <FormattedMessage id="results.hotel.card.compare.modal.share" />}
            </BlueButton>
          </div>
        </Form>
      ) : (
        <>
          <h4 className="modal-share__title secondary-font">
            <FormattedMessage id="results.hotel.card.compare.modal.comment.title" />
          </h4>
          <div className="modal-share__hotels">
            <Input.TextArea rows={4} value={comment || ''} onChange={(e) => setComment(e.target.value)} />
          </div>
          <div className="modal-share__buttons">
            <BlueButton disabled={loading} onClick={() => onDownload(comment)}>
              {loading ? <Spin /> : <FormattedMessage id="results.hotel.card.compare.modal.download" />}
            </BlueButton>
          </div>
        </>
      )}

      <ModalConfirmation
        className="modal-share"
        wrapClassName="modal-share__confirmation-wrapper"
        title="results.hotel.card.compare.modal.share"
        message={shareConfirmationMessage || 'Are you sure?'}
        yesMessage="share.continue"
        noMessage="share.cancel"
        visible={showShareConfirmation}
        disableMessageLocalization
        onCancel={onCancelShareConfirm}
        onConfirm={onConfirmShare}
      />
    </Modal>
  );
}
